import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { useModal } from 'hooks';
import api, { cooperplaceApi } from 'services/api';
import {
  fetchAllNegotiationsByPersonId,
  fetchImplements,
  fetchSelectOptions,
  fetchCostCenter,
} from 'utils/fetches';
import {
  fetchBodies,
  fetchVehicles,
} from 'v3/pages/Load/Register/Vehicle/controller';
import CheckBox from 'components/CheckBox';
import { Button, Card, Input, Select, Text } from 'v3/components';
import Space from 'v3/components/Space';
import {
  StyledCheckboxOptionsCol,
  StyledChecklistInfoRow,
  StyledFooterRow,
  StyledTextArea,
  StyledTextAreaCol,
} from '../../components/RegisterComponents/styles';
import { SortableSection } from '../../components/RegisterComponents/SortableSection';
import { QuestionsModal } from '../../components/RegisterComponents/QuestionsModal';
import { useChecklist } from '../context';
import ChecklistExpiration from '../ChecklistExpiration';

const TRAINING_BLOCKED_QUESTION_SLUGS = ['vehicle_plate', 'wagon_plate', 'code'];
const TRAINING_BLOCKED_MESSAGE =
  'Checklist de treinamento não pode incluir perguntas do tipo placa, implemento, código, com subpergunta ou que geram ocorrência.';
const NON_TRAINING_BLOCKED_MESSAGE =
  'Checklist que não seja de treinamento não pode incluir perguntas com resposta correta ou do tipo visualização de vídeo.';
const COURSE_WORKLOAD_MASK = [/\d/, /\d/, ':', /[0-5]/, /\d/];
const COURSE_WORKLOAD_OPTIONS = Array.from({ length: 48 }, (_, index) => {
  const totalMinutes = (index + 1) * 15;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
});

function isTrainingChecklistType(checklistType) {
  const slug = String(checklistType?.slug || '').toLowerCase();
  const name = String(checklistType?.name || checklistType?.label || '')
    .trim()
    .toLowerCase();

  return slug === 'training' || name === 'treinamento';
}

export function ChecklistData({ handleSave, isLoading, handleCancel }) {
  const params = useParams();
  const location = useLocation();
  const questionModal = useModal();
  const checklist = useChecklist();
  const [costCenterOptions, setCostCenterOptions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [negotiationOptions, setNegotiationOptions] = useState([]);
  const [loadingNegotiations, setLoadingNegotiations] = useState(false);
  const [vehiclesOptions, setVehiclesOptions] = useState([]);
  const [bodyOptions, setBodyOptions] = useState([]);
  const [implementsTypes, setImplementsTypes] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [branchOfficesOptions, setBranchOfficesOptions] = useState([]);
  const [groupsOptions, setGroupsOptions] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [courseWorkloadOptionsOpen, setCourseWorkloadOptionsOpen] = useState(false);
  const [renderImplement, setRenderImplement] = useState(false);
  const CHECKLIST_TYPE_SINGLE_SLUG = 'SeparateChecklist';
  const isSingleChecklist = (checklist?.data?.checklistType?.slug || '') === CHECKLIST_TYPE_SINGLE_SLUG;
  const isAvulsoChecklist = isSingleChecklist;
  const isVehicleChecklist = checklist.data.checklistType?.name === 'Checklist veículos';
  const isTrainingChecklist = isTrainingChecklistType(
    checklist?.data?.checklistType
  );
  const isCreate = location.pathname.includes('cadastrar');
  const showActiveCheckbox = !isCreate || isSingleChecklist;
  const showAppVisibleCheckbox = isSingleChecklist;

  const SERVICE_LEVELS = [
    { name: 'Frota', slug: 'PROPRIOS' },
    { name: 'Agregado – Ouro', slug: 'OURO' },
    { name: 'Agregado – Bronze', slug: 'AGREGADO' },
    { name: 'Terceiro', slug: 'TERCEIRO' },
  ];

  const [validities, setValidities] = useState(() =>
    SERVICE_LEVELS.map(({ name }) => ({
      name,
      days: 0,
      operator: 'AND',
      trips: 0,
    }))
  );

  function hasTrainingBlockedQuestions(items = []) {
    return (items || []).some(question => {
      const questionTypeSlug = String(question?.type?.slug || '').toLowerCase();

      return (
        TRAINING_BLOCKED_QUESTION_SLUGS.includes(questionTypeSlug) ||
        Boolean(question?.subQuestion?.id || question?.hasSubQuestion) ||
        Boolean(question?.createOccurrence)
      );
    });
  }

  function hasNonTrainingBlockedQuestions(items = []) {
    return (items || []).some(question => {
      const questionTypeSlug = String(question?.type?.slug || '').toLowerCase();
      return (
        questionTypeSlug === 'video_visualization' ||
        question?.hasCorrectAnswer ||
        question?.subQuestion?.hasCorrectAnswer
      );
    });
  }

  function mapTrainingRequiredQuestions(items = []) {
    return (items || []).map(item => ({
      ...item,
      required: true,
    }));
  }

  function mapServiceNameToSlug(name) {
    const level = SERVICE_LEVELS.find(l => l.name === name);
    return level?.slug || '';
  }

  const updateValidity = (index, field, value) => {
    const newValidities = validities.map((v, i) =>
      i === index ? { ...v, [field]: value } : v
    );
    setValidities(newValidities);
    checklist.setData(old => ({
      ...old,
      serviceLevels: newValidities.map(v => {
        const rawSlug = mapServiceNameToSlug(v.name);
        const backendSlug = rawSlug === 'BRONZE' ? 'AGREGADO' : rawSlug;
        return {
          serviceLevel: backendSlug,
          days: v.days,
          quantity: v.trips,
          condition: v.operator,
        };
      }),
    }));
  };

  // validade específica do avulso (dias avulsos / respostas para link)
  const updateSingleValidity = (index, field, value) => {
    if (field === 'days') {
      checklist.setData(old => ({
        ...old,
        singleDays: value,
      }));
    }
    if (field === 'trips') {
      checklist.setData(old => ({
        ...old,
        singleLinkResponses: value,
      }));
    }
  };

  useEffect(() => {
    if (!params?.id || !Array.isArray(checklist.data.serviceLevels)) return;

    const hydrated = SERVICE_LEVELS.map(({ name, slug }) => {
      const s = checklist.data.serviceLevels.find(
        x => x.slug === slug || x.serviceLevel === slug
      );
      return {
        name,
        days: s?.checklistDays ?? s?.days ?? 0,
        operator: s?.orAnd ? 'OR' : s?.condition ?? 'AND',
        trips: s?.travelQuantity ?? s?.quantity ?? 0,
      };
    });
    setValidities(hydrated);
  }, [params?.id, checklist.data.serviceLevels]);

  useEffect(() => {
    if (params?.id) {
      fetchCostCenter({ search: '' }).then(data => {
        setCostCenterOptions(data);

        const costCenterIds =
          checklist?.data?.costCenters?.map(cc => cc.id) || [];

        checklist.setData({
          ...checklist.data,
          costCentersId: costCenterIds,
        });
      });
    }
  }, [params?.id, checklist?.data?.costCenters]);

  useEffect(() => {
    fetchCostCenter({ search: '' }).then(data => {
      setCostCenterOptions(data);
    });
  }, []);

  useEffect(() => {
    if (!params?.id) {
      checklist.setData({
        required: false,
        default: false,
        defaultCostCenter: false,
        active: true,
        appVisible: false,
        name: '',
        description: '',
        checklistType: null,
        clients: [],
        negotiation: null,
        vehicleType: [],
        vehicleBodyType: [],
        branchOffices: [],
        costCentersId: [],
        serviceLevels: [],
        implementType: [],
        groups: [],
        singleDays: 0,
        singleLinkResponses: 0,
        courseWorkload: '',
      });
    }
  }, [params?.id]);

  useEffect(() => {
    let isMounted = true;

    const hasVideoMedia = question =>
      !!(
        question?.originalQuestion?.media?.full_path ||
        question?.originalQuestion?.media?.fullPath ||
        question?.originalQuestion?.media?.url
      );

    async function hydrateEditingQuestions() {
      if (!params?.id) {
        return;
      }

      const rawQuestions = checklist?.data?.questions || [];

      const hydratedQuestions = await Promise.all(
        rawQuestions.map(async question => {
          const typeSlug = String(question?.type?.slug || '').toLowerCase();
          const shouldHydrateQuestion =
            (!typeSlug || typeSlug === 'video_visualization') &&
            !hasVideoMedia(question);

          if (!shouldHydrateQuestion || !question?.id) {
            return question;
          }

          try {
            const { data } = await cooperplaceApi.get(`questions/${question.id}`);
            return {
              ...question,
              ...data,
              media: data?.media || question?.media,
              mediaId: data?.mediaId || question?.mediaId,
              originalQuestion:
                data?.originalQuestion || question?.originalQuestion,
            };
          } catch (error) {
            return question;
          }
        })
      );

      if (isMounted) {
        setQuestions(
          isTrainingChecklist
            ? mapTrainingRequiredQuestions(hydratedQuestions)
            : hydratedQuestions
        );
      }
    }

    hydrateEditingQuestions();

    return () => {
      isMounted = false;
    };
  }, [params?.id, checklist?.data?.questions, isTrainingChecklist]);

  useEffect(() => {
    if (!isTrainingChecklist) {
      return;
    }

    setQuestions(old => mapTrainingRequiredQuestions(old));
  }, [isTrainingChecklist]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  async function fetchAllNegotiationsByClient(clientId) {
    try {
      setLoadingNegotiations(true);
      const response = await fetchAllNegotiationsByPersonId({
        personId: clientId,
      });
      setNegotiationOptions(response);
    } catch (error) {
      setNegotiationOptions([]);
    } finally {
      setLoadingNegotiations(false);
    }
  }

  async function getVehicleTypes() {
    const vehicles = await fetchVehicles();
    setVehiclesOptions(vehicles);
  }

  async function getImplementTypes() {
    const response = await fetchImplements();
    setImplementsTypes(response);
  }

  async function getVehicleBodyTypes() {
    const bodies = await fetchBodies();
    setBodyOptions(bodies);
  }

  async function getChecklistTypes() {
    const response = await cooperplaceApi.get('checklists-types');
    setTypeOptions(response.data);
  }

  async function getUserBranchOffices() {
    const {
      data: { branchOffices },
    } = await cooperplaceApi.get('branch-offices/by-user');
    setBranchOfficesOptions(branchOffices);
  }

  function handleOnSearchGroup(search) {
    if (!search) return groupsOptions;
    const parsed = String(search).toLowerCase();
    return (groupsOptions || []).filter(opt =>
      String(opt?.name || '').toLowerCase().includes(parsed)
    );
  }

  async function getChecklistGroups() {
    try {
      const res = await api.get('groups', { params: { onlyActive: true } });
      setGroupsOptions(res.data || []);
    } catch (e) {
      setGroupsOptions([]);
    }
  }

  async function fetchInitalOptions() {
    try {
      await Promise.all([
        getVehicleTypes(),
        getVehicleBodyTypes(),
        getChecklistTypes(),
        getUserBranchOffices(),
        getImplementTypes(),
        getChecklistGroups(),
      ]);
    } catch (error) {
      
      checklist.showSnackbar(
        'Não foi possível carregar todas as opções de seleções. Recarregue a página',
        {
          type: 'error',
          showClose: true,
        }
      );
    }
  }

  useEffect(() => {
    fetchInitalOptions();
  }, []);

  useEffect(() => {
    if (params?.id) {
      return;
    }

    if (checklist?.data?.checklistType?.id) {
      return;
    }

    if (location?.state?.presetChecklistType !== 'training') {
      return;
    }

    const trainingType = (typeOptions || []).find(isTrainingChecklistType);

    if (!trainingType) {
      return;
    }

    checklist.setData(old => ({
      ...old,
      checklistType: trainingType,
    }));
  }, [
    params?.id,
    location?.state?.presetChecklistType,
    typeOptions,
    checklist?.data?.checklistType?.id,
    checklist.setData,
  ]);

  useEffect(() => {
    if (checklist?.data?.clients?.length === 1) {
      fetchAllNegotiationsByClient(checklist?.data?.clients[0]?.id);
    }
  }, [checklist?.data?.clients]);

  function handleSearchType(search) {
    if (!search) return typeOptions;
    const parsed = search.toLowerCase();
    return typeOptions.filter(
      option => option.name.toLowerCase().search(parsed) >= 0
    );
  }

  function handleChangeCnpjs(value) {
    if (value === null || value?.length > 1) {
      checklist.setData(old => ({ ...old, clients: value, negotiation: null }));
      setNegotiationOptions([]);
    } else {
      checklist.setData(old => ({
        ...old,
        clients: value,
      }));
    }
    if (Object.keys(checklist.errors).length > 0) {
      checklist.setErrors({});
    }
  }

  function handleChangeVehicleType(value) {
    setRenderImplement(false);
    if (value === null) {
      checklist.setData(old => ({
        ...old,
        vehicleType: [],
        vehicleBodyType: [],
        implementType: [],
      }));
      return;
    }

    const mechanicalHorse = value.find(
      ({ name }) => name === 'Cavalo Mecânico'
    );

    if (mechanicalHorse) {
      setRenderImplement(
        checklist?.data?.checklistType?.name === 'Checklist veículos'
      );
      checklist.setData(old => ({
        ...old,
        vehicleType: [mechanicalHorse],
        implementType: [],
      }));
    } else {
      checklist.setData(old => ({
        ...old,
        vehicleType: value,
        implementType: [],
      }));
    }

    if (Object.keys(checklist.errors).length > 0) {
      checklist.setErrors({});
    }
  }

  function handleOnChangeDefaultChecklist() {
    if (checklist?.data?.default === false) {
      checklist.setData(old => ({
        ...old,
        default: !old?.default,
        clients: [],
        negotiation: null,
      }));
    } else {
      checklist.setData(old => ({ ...old, default: !old?.default }));
    }
    if (Object.keys(checklist.errors).length > 0) {
      checklist.setErrors({});
    }
  }

  function handleChangeBranchOffices(value) {
    checklist.setData(old => ({ ...old, branchOffices: value || [] }));
    if (Object.keys(checklist.errors).length > 0) {
      checklist.setErrors({});
    }
  }

  function handleChangeCourseWorkload(value) {
    checklist.setData(old => ({
      ...old,
      courseWorkload: value,
    }));
    if (Object.keys(checklist.errors).length > 0) {
      checklist.setErrors({});
    }
  }

  const defaultChecklistBlocks = checklist?.data?.default;
  const shouldDisableNegotiation = checklist?.data?.clients?.length > 1;
  const defaultCostCenterBlocks = checklist?.data?.defaultCostCenter;
  const shouldBlockClientFields =  defaultChecklistBlocks || defaultCostCenterBlocks;

  useEffect(() => {
    const mechanicalHorse = (checklist?.data?.vehicleType || []).find(
      ({ name }) => name === 'Cavalo Mecânico'
    );

    setRenderImplement(
      checklist?.data?.checklistType?.name === 'Checklist veículos' &&
        !!mechanicalHorse
    );
  }, [checklist?.data?.checklistType, checklist?.data?.vehicleType]);

  return (
    <>
      <Card
        header={<Text type="header">Checklist</Text>}
        HeaderRightComponent={
          <Button onClick={() => questionModal?.open()}>
            <Text type="regular" weigth={500}>
              Incluir pergunta
            </Text>
          </Button>
        }
        loading={isLoading || checklist.loading}
      >
        <Row className="mb-3">
          <StyledCheckboxOptionsCol>
            {(showActiveCheckbox || isTrainingChecklist) && (
              <CheckBox
                label="Ativo"
                checked={checklist?.data?.active}
                value={checklist?.data?.active}
                onChange={() => {
                  checklist.setData(old => ({ ...old, active: !old?.active }));
                  if (Object.keys(checklist.errors).length > 0) {
                    checklist.setErrors({});
                  }
                }}
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                  width: 'auto',
                  fontFamily: 'Poppins',
                }}
                labelWeight="500"
              />
            )}

            {showAppVisibleCheckbox && !isTrainingChecklist && (
              <CheckBox
                label="Habilitado para APP Checklist"
                checked={!!checklist?.data?.appVisible}
                value={!!checklist?.data?.appVisible}
                onChange={() => {
                  checklist.setData(old => ({
                    ...old,
                    appVisible: !old?.appVisible,
                  }));
                  if (Object.keys(checklist.errors).length > 0) {
                    checklist.setErrors({});
                  }
                }}
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                  width: 'auto',
                  fontFamily: 'Poppins',
                }}
                labelWeight="500"
              />
            )}

            {!isSingleChecklist && !isTrainingChecklist && (
              <>
                <CheckBox
                  label="Obrigatório"
                  checked={checklist?.data?.required}
                  value={checklist?.data?.required}
                  onChange={() => {
                    checklist.setData(old => ({
                      ...old,
                      required: !old?.required,
                    }));
                    if (Object.keys(checklist.errors).length > 0) {
                      checklist.setErrors({});
                    }
                  }}
                  labelStyle={{
                    textTransform: 'capitalize',
                    color: '#464E5F',
                    width: 'auto',
                    fontFamily: 'Poppins',
                  }}
                  labelWeight="500"
                />

                <CheckBox
                  label="Checklist padrão filial"
                  checked={checklist?.data?.default}
                  value={checklist?.data?.default}
                  onChange={() => handleOnChangeDefaultChecklist()}
                  labelStyle={{
                    textTransform: 'capitalize',
                    color: '#464E5F',
                    width: 'auto',
                    fontFamily: 'Poppins',
                  }}
                  labelWeight="500"
                />

                <CheckBox
                  label="Checklist padrão Centro de custo"
                  checked={checklist.data.defaultCostCenter}
                  onChange={() =>
                    checklist.setData(old => {
                      const isNowEnabled = !old.defaultCostCenter;
                      return {
                        ...old,
                        defaultCostCenter: isNowEnabled,
                        clients: isNowEnabled ? [] : old.clients,
                        negotiation: isNowEnabled ? null : old.negotiation,
                      };
                    })
                  }
                  labelStyle={{
                    textTransform: 'capitalize',
                    color: '#464E5F',
                    width: 'auto',
                    fontFamily: 'Poppins',
                  }}
                  labelWeight="500"
                />

                <CheckBox
                  label="Motorista responde pelo APP"
                  checked={checklist?.data?.availableForDriver}
                  value={checklist?.data?.availableForDriver}
                  onChange={() => {
                    checklist.setData(old => ({
                      ...old,
                      availableForDriver: !old?.availableForDriver,
                      requiredDriverSign: !old?.availableForDriver
                        ? old.requiredDriverSign
                        : false,
                    }));
                    if (Object.keys(checklist.errors).length > 0) {
                      checklist.setErrors({});
                    }
                  }}
                  labelStyle={{
                    textTransform: 'capitalize',
                    color: '#464E5F',
                    width: 'auto',
                    fontFamily: 'Poppins',
                  }}
                  labelWeight="500"
                />

                <CheckBox
                  label="Disponibilizar no Painel Logístico"
                  checked={checklist?.data?.availableLogisticPanel}
                  value={checklist?.data?.availableLogisticPanel}
                  onChange={() => {
                    checklist.setData(old => ({
                      ...old,
                      availableLogisticPanel: !old?.availableLogisticPanel,
                    }));
                    if (Object.keys(checklist.errors).length > 0) {
                      checklist.setErrors({});
                    }
                  }}
                  labelStyle={{
                    textTransform: 'capitalize',
                    color: '#464E5F',
                    width: 'auto',
                    fontFamily: 'Poppins',
                  }}
                  labelWeight="500"
                />
              </>
            )}
          </StyledCheckboxOptionsCol>

          <StyledCheckboxOptionsCol style={{ alignItems: 'start' }}>
            {!isSingleChecklist && !isTrainingChecklist && checklist?.data?.availableForDriver && (
              <CheckBox
                label="Requer assinatura do motorista"
                checked={checklist?.data?.requiredDriverSign}
                value={checklist?.data?.requiredDriverSign}
                onChange={() => {
                  checklist.setData(old => ({
                    ...old,
                    requiredDriverSign: !old?.requiredDriverSign,
                  }));
                  if (Object.keys(checklist.errors).length > 0) {
                    checklist.setErrors({});
                  }
                }}
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                  width: 'auto',
                  fontFamily: 'Poppins',
                }}
                labelWeight="500"
              />
            )}

            {!isSingleChecklist && !isTrainingChecklist && (
              <CheckBox
                label="Requer assinatura do inspetor"
                checked={checklist?.data?.requiredInspectSign}
                value={checklist?.data?.requiredInspectSign}
                onChange={() => {
                  checklist.setData(old => ({
                    ...old,
                    requiredInspectSign: !old?.requiredInspectSign,
                  }));
                  if (Object.keys(checklist.errors).length > 0) {
                    checklist.setErrors({});
                  }
                }}
                labelStyle={{
                  textTransform: 'capitalize',
                  color: '#464E5F',
                  width: 'auto',
                  fontFamily: 'Poppins',
                }}
                labelWeight="500"
              />
            )}
          </StyledCheckboxOptionsCol>
        </Row>

        <StyledChecklistInfoRow>
          <Col md={6} sm={12}>
            <Input
              label="Título *"
              value={checklist?.data?.name}
              onChange={e => {
                checklist.setData(old => ({ ...old, name: e?.target?.value }));
                if (Object.keys(checklist.errors).length > 0) {
                  checklist.setErrors({});
                }
              }}
              error={checklist?.errors?.name}
            />
            <Select.Async
              label="Tipo de checklist *"
              value={checklist?.data?.checklistType}
              onChange={value => {
                const nextIsTrainingChecklist = isTrainingChecklistType(value);
                const hasBlockedQuestions = nextIsTrainingChecklist
                  ? hasTrainingBlockedQuestions(questions)
                  : hasNonTrainingBlockedQuestions(questions);

                if (hasBlockedQuestions) {
                  const blockedMessage = nextIsTrainingChecklist
                    ? TRAINING_BLOCKED_MESSAGE
                    : NON_TRAINING_BLOCKED_MESSAGE;

                  checklist.showSnackbar(blockedMessage, {
                    type: 'error',
                    showClose: true,
                  });
                  checklist.setErrors(old => ({
                    ...old,
                    questions: blockedMessage,
                  }));
                  return;
                }

                checklist.setData(old => {
                  const isVehicleChecklistSelected =
                    value?.name === 'Checklist veículos';
                  if (nextIsTrainingChecklist) {
                    setQuestions(oldQuestions =>
                      mapTrainingRequiredQuestions(oldQuestions)
                    );
                  }
                  return {
                    ...old,
                    checklistType: value,
                    courseWorkload: nextIsTrainingChecklist
                      ? old.courseWorkload || ''
                      : '',
                    implementType: isVehicleChecklistSelected
                      ? old.implementType
                      : [],
                  };
                });

                if (Object.keys(checklist.errors).length > 0) {
                  checklist.setErrors({});
                }
              }}
              onSearch={search => handleSearchType(search)}
              getOptionLabel={option => option?.name}
              getOptionValue={option => option?.id}
              options={typeOptions}
              error={checklist?.errors?.type}
            />
            {isTrainingChecklist && (
              <div style={{ position: 'relative' }}>
                <Input
                  label="Carga horária do treinamento *"
                  placeholder="00:00"
                  value={checklist?.data?.courseWorkload || ''}
                  masked
                  guide={false}
                  mask={COURSE_WORKLOAD_MASK}
                  rightIcon={
                    <span style={{ color: '#6B7280', fontSize: 12 }}>▼</span>
                  }
                  rightIconAction={() =>
                    setCourseWorkloadOptionsOpen(open => !open)
                  }
                  onFocus={() => setCourseWorkloadOptionsOpen(true)}
                  onBlur={() => {
                    setTimeout(() => setCourseWorkloadOptionsOpen(false), 150);
                  }}
                  onChange={e => {
                    handleChangeCourseWorkload(e?.target?.value);
                  }}
                  error={checklist?.errors?.courseWorkload}
                />
                {courseWorkloadOptionsOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 20,
                      marginTop: 4,
                      maxHeight: 184,
                      overflowY: 'auto',
                      background: '#FFFFFF',
                      border: '1px solid #DCE2EA',
                      borderRadius: 4,
                      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.12)',
                    }}
                  >
                    {COURSE_WORKLOAD_OPTIONS.map(option => (
                      <button
                        key={option}
                        type="button"
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => {
                          handleChangeCourseWorkload(option);
                          setCourseWorkloadOptionsOpen(false);
                        }}
                        style={{
                          width: '100%',
                          height: 34,
                          padding: '0 12px',
                          border: 0,
                          background:
                            checklist?.data?.courseWorkload === option
                              ? '#EEF6FF'
                              : '#FFFFFF',
                          color: '#334155',
                          textAlign: 'left',
                          fontFamily: 'Poppins',
                          fontSize: 13,
                          cursor: 'pointer',
                        }}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Col>
          <StyledTextAreaCol>
            <StyledTextArea
              label="Descrição do checklist *"
              value={checklist?.data?.description}
              onChange={e => {
                const content = e?.target?.value;
                checklist.setData(old => ({
                  ...old,
                  description: content,
                }));
                if (Object.keys(checklist.errors).length > 0) {
                  checklist.setErrors({});
                }
              }}
              containerStyle={{ display: 'flex', flexDirection: 'column' }}
              containerInsideStyle={{ flex: 1 }}
              error={checklist?.errors?.description}
            />
          </StyledTextAreaCol>
        </StyledChecklistInfoRow>

        {!isAvulsoChecklist && !isTrainingChecklist && (
          <Row className="my-3">
            <Col md={6} sm={12}>
              <Select.Async
                label="Cliente - CNPJ"
                value={checklist?.data?.clients}
                placeholder="Cliente - CNPJ"
                multiple
                horizontal={windowWidth <= 992}
                modalHeading="CNPJs"
                modalBodyTitle="Selecione um cnpj"
                onSearch={search =>
                  fetchSelectOptions('persons/customers', {
                    search,
                  })
                }
                onChange={value => handleChangeCnpjs(value)}
                getOptionLabel={option => {
                  if (option?.label) return option?.label;
                  return `${option?.socialName} - ${option?.cpfCnpj}`;
                }}
                getOptionValue={option => option.id}
                selectValuesContainerMaxHeight="128px"
                selectValuesContainerOverflowY="scroll"
                error=""
                isDisabled={shouldBlockClientFields}
                tooltip={
                  shouldBlockClientFields &&
                  'Checklist padrão não aceita cadastro de CNPJs'
                }
              />
            </Col>

            <Col md={6} sm={12}>
              <Select
                label="Negociação"
                getOptionLabel={option => option.name}
                getOptionValue={option => option.id}
                value={checklist?.data.negotiation}
                options={negotiationOptions}
                onChange={value => {
                  checklist.setData(old => ({ ...old, negotiation: value }));
                  if (Object.keys(checklist.errors).length > 0) {
                    checklist.setErrors({});
                  }
                }}
                noOptionsMessageText="Nenhuma negociação disponível"
                loading={loadingNegotiations}
                isDisabled={shouldDisableNegotiation || shouldBlockClientFields}
                tooltip={
                  defaultChecklistBlocks
                    ? 'Checklist padrão (filial) não aceita cadastro de negociação'
                    : defaultCostCenterBlocks
                    ? 'Checklist padrão (centro de custo) não aceita cadastro de negociação'
                    : shouldDisableNegotiation
                    ? 'Grupo de CNPJ não aceita cadastro de negociação'
                    : ''
                }
                error={checklist?.errors?.negotiation}
              />
            </Col>
          </Row>
        )}
        {isTrainingChecklist ? null : isAvulsoChecklist ? (
          <Row>
            <Col md={6} sm={12} className="d-flex flex-column h-100">
              <div className="mb-3">
                <Select.Async
                  label="Grupos do checklist"
                  multiple
                  value={checklist?.data?.groups || []}
                  onSearch={handleOnSearchGroup}
                  onChange={value => {
                    checklist.setData(old => ({ ...old, groups: value || [] }));
                    if (Object.keys(checklist.errors).length > 0) checklist.setErrors({});
                  }}
                  getOptionLabel={opt => opt.name}
                  getOptionValue={opt => opt.id}
                  options={groupsOptions}
                  error={checklist?.errors?.groups}
                />
              </div>
            </Col>

            <Col md={6} sm={12} className="d-flex flex-column h-100">
              <div className="mb-3">
                <label
                  style={{
                    display: 'block',
                    marginBottom: 8,
                    color: '#464e5f',
                    fontWeight: 500,
                    fontSize: 12,
                    lineHeight: '20px',
                    fontFamily: 'Poppins',
                  }}
                >
                  Validade checklist
                </label>

                <ChecklistExpiration
                  index={0}
                  serviceLevel={{
                    name: '',
                    days: checklist.data.singleDays || 0,
                    operator: 'AND',
                    trips: checklist.data.singleLinkResponses || 0,
                  }}
                  updateValidity={updateSingleValidity}
                  isSingleChecklist
                />
              </div>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={6} sm={12} className="d-flex flex-column h-100">
              <div className="mb-3">
                <Select
                  label="Tipo de veículo"
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.id}
                  options={vehiclesOptions}
                  multiple
                  value={checklist?.data?.vehicleType}
                  onChange={handleChangeVehicleType}
                />
              </div>

              {renderImplement && (
                <div className="mb-3">
                  <Select
                    label="Tipo de implemento"
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    options={implementsTypes}
                    multiple
                    value={checklist?.data?.implementType}
                    error={checklist?.errors?.implementType}
                    onChange={value => {
                      checklist.setData(old => ({
                        ...old,
                        implementType: value || [],
                      }));
                      if (Object.keys(checklist.errors).length > 0) {
                        checklist.setErrors({});
                      }
                    }}
                  />
                </div>
              )}

              <div className="mb-3">
                <Select
                  label={`Tipo de carroceria${
                    renderImplement ? ' do implemento' : ''
                  }`}
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.id}
                  multiple
                  options={bodyOptions}
                  value={checklist?.data?.vehicleBodyType}
                  onChange={value => {
                    checklist.setData(old => ({
                      ...old,
                      vehicleBodyType: value || [],
                    }));
                    if (Object.keys(checklist.errors).length > 0) {
                      checklist.setErrors({});
                    }
                  }}
                  error={checklist?.errors?.vehicleBodyType}
                />
              </div>
            </Col>

            <Col md={6} sm={12} className="d-flex flex-column h-100">
              <div className="mb-3">
                <Select.Async
                  label={`Centro de custo${
                    checklist.data.defaultCostCenter ? ' *' : ''
                  }`}
                  multiple
                  onSearch={search => fetchCostCenter({ search })}
                  value={costCenterOptions.filter(opt =>
                    (checklist.data.costCentersId || []).includes(opt.id)
                  )}
                  onChange={selectedOptions => {
                    checklist.setData(old => ({
                      ...old,
                      costCentersId: (selectedOptions || []).map(opt => opt.id),
                    }));
                    if (checklist.errors.costCentersId) {
                      checklist.setErrors({
                        ...checklist.errors,
                        costCentersId: null,
                      });
                    }
                  }}
                  getOptionLabel={opt => opt.name}
                  getOptionValue={opt => opt.id}
                  error={checklist.errors.costCentersId}
                  styles={{
                    valueContainer: base => ({
                      ...base,
                      flexWrap: 'wrap',
                      maxHeight: 'auto',
                      minHeight: 38,
                      overflowY: 'auto',
                    }),
                    control: base => ({
                      ...base,
                      minHeight: 'auto',
                    }),
                    multiValue: base => ({
                      ...base,
                      margin: '4px',
                    }),
                  }}
                />
              </div>

              {!isTrainingChecklist && (
                <div className="mb-3">
                  <Select
                    label="Filiais *"
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    options={branchOfficesOptions}
                    multiple
                    value={checklist?.data?.branchOffices}
                    onChange={handleChangeBranchOffices}
                    error={checklist?.errors?.branchOffices}
                  />
                </div>
              )}

              {isVehicleChecklist && (
                <div className="mb-3">
                  <>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: 8,
                        color: '#464e5f',
                        fontWeight: 500,
                        fontSize: 12,
                        lineHeight: '20px',
                        fontFamily: 'Poppins',
                      }}
                    >
                      Validade Checklist
                    </label>

                    {validities.map((serviceLevel, i) => (
                      <ChecklistExpiration
                        key={serviceLevel.name || i}
                        index={i}
                        serviceLevel={serviceLevel}
                        updateValidity={updateValidity}
                      />
                    ))}
                  </>
                </div>
              )}
            </Col>
          </Row>
        )}

        <SortableSection
          questions={questions || []}
          setQuestions={setQuestions}
          forceRequired={isTrainingChecklist}
        />

        <StyledFooterRow>
          <Space x="0.5rem">
            <Button
              variant="secondary"
              disabled={isLoading}
              onClick={handleCancel}
            >
              <Text weight={500}>CANCELAR</Text>
            </Button>

            <Button
              disabled={questions?.length === 0}
              onClick={() => handleSave(questions)}
              loading={isLoading}
              title={
                questions?.length === 0 &&
                'Preencha campos obrigatórios e selecione perguntas para cadastrar checklist'
              }
            >
              <Text weight={500}>SALVAR</Text>
            </Button>
          </Space>
        </StyledFooterRow>
      </Card>

      <QuestionsModal
        isOpen={questionModal?.isOpen}
        onClose={() => questionModal.close()}
        setQuestions={setQuestions}
        questions={questions}
        isTrainingChecklist={isTrainingChecklist}
      />
    </>
  );
}
